import type {
  AiClarifyOption,
  AiResponse,
  AiTask,
} from '@store/reducers/ai/types'

import type { AiClarifyData } from './types'

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

export const isAiResponse = (value: unknown): value is AiResponse =>
  isRecord(value) &&
  typeof value.assistant_text === 'string' &&
  Array.isArray(value.actions)

export const getTaskFromResponse = (value: unknown): AiTask | null => {
  if (!isRecord(value) || !isRecord(value.task)) {
    return null
  }

  const task = value.task as Record<string, unknown>
  if (typeof task.id !== 'string' || typeof task.status !== 'string') {
    return null
  }

  return task as unknown as AiTask
}

export const normalizeTaskResult = (value: unknown): AiResponse | null => {
  if (typeof value === 'string') {
    try {
      return normalizeTaskResult(JSON.parse(value))
    } catch {
      return null
    }
  }

  if (isAiResponse(value)) {
    return value
  }

  if (isRecord(value) && 'result' in value) {
    return normalizeTaskResult(value.result)
  }

  return null
}

const parseClarifyOptions = (rawOptions: unknown): AiClarifyOption[] => {
  if (!Array.isArray(rawOptions)) {
    return []
  }

  return rawOptions.reduce<AiClarifyOption[]>((result, option, index) => {
    if (typeof option === 'string' && option.trim().length > 0) {
      result.push({ id: String(index), label: option })
      return result
    }

    if (
      isRecord(option) &&
      typeof option.label === 'string' &&
      option.label.trim().length > 0
    ) {
      const id = typeof option.id === 'string' ? option.id : String(index)
      result.push({ id, label: option.label })
    }

    return result
  }, [])
}

export const getClarifyDataFromTask = (task: AiTask): AiClarifyData | null => {
  const history = task.metadata?.history
  if (!Array.isArray(history)) {
    return null
  }

  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i]
    if (!isRecord(entry) || entry.agent !== 'clarifying') {
      continue
    }

    const { data } = entry
    if (!isRecord(data) || typeof data.question !== 'string') {
      continue
    }

    if (data.question.trim().length === 0 || typeof data.answer === 'string') {
      return null
    }

    return {
      question: data.question,
      options: parseClarifyOptions(data.options),
    }
  }

  return null
}
